import React, { createContext, useContext, useCallback, useMemo, useState } from 'react';

const STORAGE_KEY = 'contify_compras_purchase_site';

const ComprasPurchaseSiteContext = createContext(null);

function readStoredSession() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return { site: null, view: 'without' };
    }
    const parsed = JSON.parse(raw);
    if (parsed?.view === 'without') {
      return { site: null, view: 'without' };
    }
    if (parsed?.id && parsed?.name) {
      return {
        site: {
          id: parsed.id,
          code: parsed.code || '',
          name: parsed.name || '',
          warehouse_ids: Array.isArray(parsed.warehouse_ids) ? parsed.warehouse_ids : [],
        },
        view: 'active',
      };
    }
  } catch {
    /* ignore */
  }
  return { site: null, view: 'without' };
}

function writeStoredSession(site, view) {
  if (view === 'without') {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ view: 'without' }));
    return;
  }
  if (!site?.id) {
    sessionStorage.removeItem(STORAGE_KEY);
    return;
  }
  const warehouseIds = (site.warehouses || [])
    .map((w) => w.id)
    .filter((id) => id != null);
  sessionStorage.setItem(
    STORAGE_KEY,
    JSON.stringify({
      view: 'active',
      id: site.id,
      code: site.code || '',
      name: site.name || '',
      warehouse_ids: site.warehouse_ids?.length ? site.warehouse_ids : warehouseIds,
    })
  );
}

export function ComprasPurchaseSiteProvider({ children }) {
  const initial = readStoredSession();
  const [selectedPurchaseSite, setSelectedPurchaseSiteState] = useState(initial.site);
  const [comprasSiteView, setComprasSiteViewState] = useState(initial.view);

  const setSelectedPurchaseSite = useCallback((site) => {
    if (!site?.id) {
      writeStoredSession(null, 'without');
      setSelectedPurchaseSiteState(null);
      setComprasSiteViewState('without');
      return;
    }
    const warehouseIds = (site.warehouses || [])
      .map((w) => w.id)
      .filter((id) => id != null);
    const payload = {
      id: site.id,
      code: site.code || '',
      name: site.name || '',
      warehouse_ids: site.warehouse_ids?.length ? site.warehouse_ids : warehouseIds,
    };
    writeStoredSession(payload, 'active');
    setSelectedPurchaseSiteState(payload);
    setComprasSiteViewState('active');
  }, []);

  const selectWithoutSiteMode = useCallback(() => {
    writeStoredSession(null, 'without');
    setSelectedPurchaseSiteState(null);
    setComprasSiteViewState('without');
  }, []);

  const setComprasSiteView = useCallback((view) => {
    if (view === 'without') {
      selectWithoutSiteMode();
      return;
    }
    setComprasSiteViewState('active');
    if (selectedPurchaseSite?.id) {
      writeStoredSession(selectedPurchaseSite, 'active');
    }
  }, [selectWithoutSiteMode, selectedPurchaseSite]);

  const clearSelectedPurchaseSite = useCallback(() => {
    sessionStorage.removeItem(STORAGE_KEY);
    setSelectedPurchaseSiteState(null);
    setComprasSiteViewState('without');
  }, []);

  const isWithoutSiteMode = comprasSiteView === 'without';

  const value = useMemo(
    () => ({
      selectedPurchaseSite,
      comprasSiteView,
      isWithoutSiteMode,
      setSelectedPurchaseSite,
      selectWithoutSiteMode,
      setComprasSiteView,
      clearSelectedPurchaseSite,
    }),
    [
      selectedPurchaseSite,
      comprasSiteView,
      isWithoutSiteMode,
      setSelectedPurchaseSite,
      selectWithoutSiteMode,
      setComprasSiteView,
      clearSelectedPurchaseSite,
    ]
  );

  return (
    <ComprasPurchaseSiteContext.Provider value={value}>
      {children}
    </ComprasPurchaseSiteContext.Provider>
  );
}

export function useComprasPurchaseSite() {
  const ctx = useContext(ComprasPurchaseSiteContext);
  if (!ctx) {
    throw new Error('useComprasPurchaseSite must be used within ComprasPurchaseSiteProvider');
  }
  return ctx;
}
